/**
 * Consumer map filters — pure logic for the /map filter rail.
 *
 * Filters run client-side over the already-loaded hospital set (≤ maxMarkers),
 * so every change is instant. Filtered-out hospitals are DIMMED on the map, not
 * removed — context stays, focus shifts. State round-trips through URL search
 * params (f_* keys) so filtered views are shareable links.
 */
import type { Hospital } from "@/types/map";

export interface MapFilters {
  /** Letter grades to show; empty = all. */
  grades: string[];
  /** Minimum CMS overall star rating (1–5); null = any. */
  minStars: number | null;
  /** Only hospitals with emergency services. */
  erOnly: boolean;
  /** Hospital-kind buckets to show; empty = all. */
  types: string[];
  /** Ownership bucket. */
  ownership: "any" | "nonprofit" | "forprofit" | "government";
  /** Bed-size buckets to show; empty = all. */
  bedSizes: string[];
  /** Scenario mode: max blended price; null = any. */
  priceMax: number | null;
}

export const DEFAULT_FILTERS: MapFilters = {
  grades: [],
  minStars: null,
  erOnly: false,
  types: [],
  ownership: "any",
  bedSizes: [],
  priceMax: null,
};

/** Consumer-facing hospital-kind buckets from CMS hospital_type strings. */
export function typeBucket(t: string | undefined | null): string {
  if (!t) return "other";
  const s = t.toLowerCase();
  if (s.startsWith("acute care")) return "acute";
  if (s.includes("critical access") || s.includes("rural emergency")) return "rural";
  if (s.startsWith("psychiatric")) return "psych";
  if (s.startsWith("children")) return "children";
  return "other";
}

/** Nonprofit / for-profit / government buckets from CMS ownership strings. */
export function ownershipBucket(o: string | undefined | null): string {
  if (!o) return "other";
  const s = o.toLowerCase();
  if (s.startsWith("voluntary non-profit")) return "nonprofit";
  if (s === "proprietary" || s === "physician") return "forprofit";
  if (s.startsWith("government") || s.startsWith("tribal")) return "government";
  return "other";
}

/** S <100 beds · M 100–399 · L 400+. */
export function bedBucket(n: number | undefined | null): string | null {
  if (n == null || n <= 0) return null;
  if (n < 100) return "S";
  if (n < 400) return "M";
  return "L";
}

/** How many facets are actively narrowing (for the filter-button badge). */
export function countActiveFilters(f: MapFilters): number {
  let n = 0;
  if (f.grades.length) n++;
  if (f.minStars != null) n++;
  if (f.erOnly) n++;
  if (f.types.length) n++;
  if (f.ownership !== "any") n++;
  if (f.bedSizes.length) n++;
  if (f.priceMax != null) n++;
  return n;
}

/**
 * Does a hospital pass the active filters?
 * `grade` / `scenarioPrice` come from the map's derived per-hospital data
 * (grade differs between rankings mode and scenario mode).
 */
export function hospitalPassesFilters(
  h: Hospital,
  f: MapFilters,
  ctx: { grade: string | null; scenarioPrice: number | null }
): boolean {
  if (f.grades.length && (!ctx.grade || !f.grades.includes(ctx.grade))) return false;
  if (f.minStars != null && (h.cms_overall_rating == null || h.cms_overall_rating < f.minStars)) return false;
  if (f.erOnly && h.emergency_services !== true) return false;
  if (f.types.length && !f.types.includes(typeBucket(h.hospital_type))) return false;
  if (f.ownership !== "any" && ownershipBucket(h.ownership) !== f.ownership) return false;
  if (f.bedSizes.length) {
    const b = bedBucket(h.bed_count);
    if (!b || !f.bedSizes.includes(b)) return false;
  }
  if (f.priceMax != null && ctx.scenarioPrice != null && ctx.scenarioPrice > f.priceMax) return false;
  return true;
}

// ── URL round-trip ──────────────────────────────────────────────────────────

export function filtersFromParams(sp: URLSearchParams): MapFilters {
  const list = (k: string) => (sp.get(k) ? sp.get(k)!.split(",").filter(Boolean) : []);
  const own = sp.get("f_own");
  return {
    grades: list("f_g").map(g => g.toUpperCase()).filter(g => "ABCDF".includes(g)),
    minStars: sp.get("f_stars") ? Math.min(5, Math.max(1, Number(sp.get("f_stars")))) || null : null,
    erOnly: sp.get("f_er") === "1",
    types: list("f_type"),
    ownership: own === "nonprofit" || own === "forprofit" || own === "government" ? own : "any",
    bedSizes: list("f_bed").map(b => b.toUpperCase()).filter(b => "SML".includes(b)),
    priceMax: sp.get("f_pmax") ? Number(sp.get("f_pmax")) || null : null,
  };
}

/** Writes f_* keys onto a copy of the given params (preserves scenario etc.). */
export function filtersToParams(f: MapFilters, base: URLSearchParams): URLSearchParams {
  const sp = new URLSearchParams(base);
  const setOrDel = (k: string, v: string | null) => (v ? sp.set(k, v) : sp.delete(k));
  setOrDel("f_g", f.grades.length ? f.grades.join(",") : null);
  setOrDel("f_stars", f.minStars != null ? String(f.minStars) : null);
  setOrDel("f_er", f.erOnly ? "1" : null);
  setOrDel("f_type", f.types.length ? f.types.join(",") : null);
  setOrDel("f_own", f.ownership !== "any" ? f.ownership : null);
  setOrDel("f_bed", f.bedSizes.length ? f.bedSizes.join(",") : null);
  setOrDel("f_pmax", f.priceMax != null ? String(Math.round(f.priceMax)) : null);
  return sp;
}
